import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';

interface Setting {
  key: string;
  value: string;
  is_file: boolean;
  file_type: string | null;
}

interface OrganizationState {
  settings: Record<string, Setting>;
  isLoading: boolean;
  error: string | null;
}

export const fetchOrganizationSettings = createAsyncThunk(
  'organization/fetchSettings',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/settings`
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganizationSettings = createAsyncThunk(
  'organization/updateSettings',
  async (
    {
      organizationId,
      settings,
    }: { organizationId: number; settings: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${organizationId}/settings`,
        settings,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState: OrganizationState = {
  settings: {},
  isLoading: false,
  error: null,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchOrganizationSettings.fulfilled,
        (state, action: PayloadAction<Setting[]>) => {
          state.isLoading = false;
          state.settings = action.payload.reduce(
            (acc, setting) => {
              acc[setting.key] = setting;
              return acc;
            },
            {} as Record<string, Setting>
          );
          state.error = null;
        }
      )
      .addCase(fetchOrganizationSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string | null;
      })
      .addCase(updateOrganizationSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateOrganizationSettings.fulfilled,
        (
          state,
          action: PayloadAction<{ success: boolean; message: string }>
        ) => {
          state.isLoading = false;
          state.error = null;
        }
      )
      .addCase(updateOrganizationSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string | null;
      });
  },
});

export default organizationSlice.reducer;
